import { Avatar } from "@/components/Common/Avatar";
import { PatientInfoHoverCard } from "@/components/Patient/PatientInfoHoverCard";
import {
  getDisplayIdentifiers,
  getDisplayIdentifierValue,
  isUhidIdentifier,
} from "@/components/Patient/utils";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { EncounterRead } from "@/types/emr/encounter/encounter";
import {
  PatientListRead,
  PatientRead,
  PublicPatientRead,
} from "@/types/emr/patient/patient";
import { formatScheduleResourceName } from "@/types/scheduling/schedule";
import { formatPatientAge } from "@/Utils/utils";
import { ChevronDown } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { getDisplayedDoctorAppointment } from "./patientHoverCardUtils";

interface PatientHoverCardProps {
  patient: PublicPatientRead | PatientListRead | PatientRead;
  facilityId?: string;
  disabled?: boolean;
  interactionIdentifier?: string | null;
  encounter?: EncounterRead;
}

export function PatientHoverCard({
  patient,
  facilityId,
  disabled = false,
  interactionIdentifier,
  encounter,
}: PatientHoverCardProps) {
  return (
    <>
      {/* Mobile Drawer */}
      <Drawer>
        <DrawerTrigger
          disabled={disabled}
          className="lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <PatientHoverCardTrigger
            patient={patient}
            disabled={disabled}
            interactionIdentifier={interactionIdentifier}
            encounter={encounter}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col p-4 gap-4">
          <PatientInfoHoverCard
            patient={patient}
            facilityId={facilityId || ""}
          />
        </DrawerContent>
      </Drawer>

      {/* Desktop Popover */}
      <Popover>
        <PopoverTrigger
          disabled={disabled}
          className="hidden lg:flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-10 focus-visible:ring-offset-background"
        >
          <PatientHoverCardTrigger
            patient={patient}
            disabled={disabled}
            interactionIdentifier={interactionIdentifier}
            encounter={encounter}
          />
        </PopoverTrigger>
        <PopoverContent
          className="flex flex-col border border-gray-200 shadow-lg p-4 rounded-md gap-4 w-100"
          side="bottom"
          align="start"
        >
          <PatientInfoHoverCard
            patient={patient}
            facilityId={facilityId || ""}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}

interface PatientHoverCardTriggerProps {
  patient: PublicPatientRead | PatientListRead | PatientRead;
  disabled?: boolean;
  interactionIdentifier?: string | null;
  encounter?: EncounterRead;
}

function PatientHoverCardTrigger({
  patient,
  disabled = false,
  interactionIdentifier,
  encounter,
}: PatientHoverCardTriggerProps) {
  const { t } = useTranslation();
  const resolvedInteractionIdentifier =
    interactionIdentifier !== undefined
      ? interactionIdentifier
      : "latest_encounter_identifier" in patient
        ? patient.latest_encounter_identifier
        : undefined;
  const displayIdentifiers =
    "instance_identifiers" in patient
      ? getDisplayIdentifiers(
          patient.instance_identifiers,
          "facility_identifiers" in patient ? patient.facility_identifiers : [],
        )
      : [];
  const uhidIdentifier = displayIdentifiers.find(isUhidIdentifier);
  const otherIdentifiers = displayIdentifiers.filter(
    (identifier) => !isUhidIdentifier(identifier),
  );

  const doctorAppointment = getDisplayedDoctorAppointment(encounter);
  const doctorName = doctorAppointment
    ? formatScheduleResourceName(doctorAppointment)
    : null;

  return (
    <div
      data-slot="patient-info-hover-card-trigger"
      className={cn(
        "flex w-fit gap-3 items-center rounded-md",
        !disabled && "hover:bg-gray-50 active:bg-gray-50 cursor-pointer",
      )}
    >
      <div className="size-12">
        <Avatar name={patient.name} />
      </div>

      <div className="flex flex-col">
        <div className="flex flex-row gap-2 items-center">
          <h5
            className={cn(
              "text-lg font-semibold whitespace-nowrap",
              !disabled && "underline",
            )}
          >
            {patient.name}
          </h5>
          {!disabled && <ChevronDown size={16} />}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-gray-700">
          <span>{formatPatientAge(patient, true)}</span>
          <span>•</span>
          <span>{t(`GENDER__${patient.gender}`)}</span>
          {!disabled && uhidIdentifier && (
            <Fragment key={uhidIdentifier.config.id}>
              <span>•</span>
              <span>
                {t(uhidIdentifier.config.config.display)}:{" "}
                {getDisplayIdentifierValue(uhidIdentifier)}
              </span>
            </Fragment>
          )}
          {!disabled && resolvedInteractionIdentifier && (
            <>
              <span>•</span>
              <span>
                {t("visit_id")}: {resolvedInteractionIdentifier}
              </span>
            </>
          )}
        </div>
        {!disabled && otherIdentifiers.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-gray-700">
            {otherIdentifiers.map((identifier, index) => (
              <Fragment key={identifier.config.id}>
                {index > 0 && <span>•</span>}
                <span>
                  {identifier.config.config.display}:{" "}
                  {getDisplayIdentifierValue(identifier)}
                </span>
              </Fragment>
            ))}
          </div>
        )}
        {doctorName !== null && (
          <div className="flex items-start text-sm font-medium text-gray-700">
            {t("doctor")}: {doctorName}
          </div>
        )}
      </div>
    </div>
  );
}
